/**
 * Clone Template Dialog Component
 * Dialog to clone a template with new name and description
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  CircularProgress,
  LinearProgress,
  Alert,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useGlobalConfig } from 'context/GlobalContext';
import { BglPrimarySmallButton, BglSecondarySmallButton } from 'components/button/BglButtons';
import { ConfigRegistryApis } from '../api/configRegistryAPI';
import { Template } from '../types/configRegistryTypes';

interface CloneTemplateDialogProps {
  open: boolean;
  onClose: () => void;
  template: Template;
  /** Receives the new template's documentId so callers can navigate to it */
  onCloned: (clonedDocumentId?: string) => void;
}

export const CloneTemplateDialog = ({
  open,
  onClose,
  template,
  onCloned,
}: CloneTemplateDialogProps) => {
  const { rc } = useGlobalConfig();
  
  const [name, setName] = useState(`${template.name} (Copy)`);
  const [description, setDescription] = useState(template.description || '');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed the fields from the template being cloned every time the dialog opens.
  // Callers keep this mounted and swap the `template` prop, so the useState
  // initializers only ever ran for the first template — cloning a second one
  // showed the first one's name.
  useEffect(() => {
    if (!open) return;
    setName(`${template.name} (Copy)`);
    setDescription(template.description || '');
    setError(null);
  }, [open, template.documentId, template.name, template.description]);

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleClone = async () => {
    if (!name.trim()) {
      setError('Template name is required');
      return;
    }
    
    setIsSaving(true);
    setError(null);
    try {
      const response = await ConfigRegistryApis.cloneTemplate(
        rc,
        template.documentId,
        name.trim(),
        description.trim() || undefined
      );
      
      if (response.error) {
        setError(response.error.message || 'Failed to clone template');
      } else {
        // Body is { data: <template>, message } and CMSApiClient returns it
        // verbatim, so the clone sits at .data — tolerate an extra envelope
        const body = response.data as any;
        onCloned(body?.documentId ?? body?.data?.documentId);
        handleClose();
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred while cloning');
    } finally {
      setIsSaving(false);
    }
  };

  const valueCount = template.valueCount ?? template.items?.length ?? 0;
  // Server writes one row per config; a big template takes a while and the
  // request must not be abandoned midway, leaving a half-populated clone
  const isSlowClone = valueCount > 200;

  return (
    <Dialog
      open={open}
      onClose={isSaving ? undefined : handleClose}
      disableEscapeKeyDown={isSaving}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ContentCopyIcon color="primary" />
        Clone Template
      </DialogTitle>
      
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Create a copy of "{template.name}" with all its linked configs and values.
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="New Template Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
            error={!name.trim()}
            helperText={!name.trim() ? 'Name is required' : ''}
          />
          
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={3}
            placeholder="Optional description for the cloned template"
          />
        </Box>
        
        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Source Template:</strong> {template.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Configs to copy:</strong> {valueCount}
          </Typography>
        </Box>

        {isSlowClone && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Copying {valueCount} configs can take a few minutes. Keep this dialog open
            until it finishes — closing it early leaves a partly-filled template.
          </Alert>
        )}

        {isSaving && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Copying {valueCount} config value{valueCount === 1 ? '' : 's'}…
            </Typography>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <BglSecondarySmallButton onClick={handleClose} disabled={isSaving}>
          Cancel
        </BglSecondarySmallButton>
        <BglPrimarySmallButton
          onClick={handleClone}
          disabled={!name.trim() || isSaving}
          startIcon={isSaving ? <CircularProgress size={16} /> : <ContentCopyIcon />}
        >
          {isSaving ? 'Cloning...' : 'Clone Template'}
        </BglPrimarySmallButton>
      </DialogActions>
    </Dialog>
  );
};

export default CloneTemplateDialog;

